import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Palette, Save, RotateCcw, Loader2, Type } from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useStyling } from "@/context/StylingContext";
import PageHeader from "@/components/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

const FONT_OPTIONS = ["Inter", "Roboto", "DM Sans", "Public Sans", "Space Grotesk"];

// Convert "H S% L%" ↔ "#RRGGBB" — simplistic but sufficient for the picker.
function hslStringToHex(hsl) {
  if (!hsl) return "#4f46e5";
  const [h, s, l] = hsl.replace(/%/g, "").split(/\s+/).map(Number);
  const sN = s / 100, lN = l / 100;
  const c = (1 - Math.abs(2 * lN - 1)) * sN;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = lN - c / 2;
  let r = 0, g = 0, b = 0;
  if (h < 60)      [r, g, b] = [c, x, 0];
  else if (h < 120)[r, g, b] = [x, c, 0];
  else if (h < 180)[r, g, b] = [0, c, x];
  else if (h < 240)[r, g, b] = [0, x, c];
  else if (h < 300)[r, g, b] = [x, 0, c];
  else             [r, g, b] = [c, 0, x];
  const to255 = (v) => Math.round((v + m) * 255);
  return "#" + [to255(r), to255(g), to255(b)].map((v) => v.toString(16).padStart(2, "0")).join("");
}
function hexToHslString(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return "0 0% 0%";
  const n = parseInt(m[1], 16);
  const r = ((n >> 16) & 0xff) / 255, g = ((n >> 8) & 0xff) / 255, b = (n & 0xff) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0; const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4;
    }
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

export default function Branding() {
  const { hasPermission } = useAuth();
  const { applyBranding } = useStyling();
  const navigate = useNavigate();
  const canWrite = hasPermission("settings.branding.write");
  const canReset = hasPermission("settings.branding.reset");

  const [form, setForm] = useState(null);
  const [baseline, setBaseline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/branding");
      const shape = {
        primary_hsl: data.primary_hsl,
        accent_hsl: data.accent_hsl,
        font_family: data.font_family,
      };
      setForm(shape);
      setBaseline(shape);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Live-preview: apply the in-flight form to StylingContext so the
  // page reflects the pending change without needing to save first.
  useEffect(() => {
    if (form) applyBranding(form);
  }, [form, applyBranding]);

  const dirty = baseline && JSON.stringify(form) !== JSON.stringify(baseline);

  const handleSave = async () => {
    if (!canWrite || !form) return;
    setSaving(true);
    try {
      const { data } = await api.patch("/settings/branding", form);
      const shape = {
        primary_hsl: data.primary_hsl,
        accent_hsl: data.accent_hsl,
        font_family: data.font_family,
      };
      setForm(shape); setBaseline(shape);
      applyBranding(shape);
      toast.success("Branding saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  const handleReset = async () => {
    if (!canReset) return;
    if (!window.confirm("Reset branding to defaults? Unsaved changes lost.")) return;
    try {
      const { data } = await api.post("/settings/branding/reset");
      const shape = {
        primary_hsl: data.primary_hsl,
        accent_hsl: data.accent_hsl,
        font_family: data.font_family,
      };
      setForm(shape); setBaseline(shape);
      applyBranding(shape);
      toast.success("Branding reset");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleCancel = () => { if (baseline) { setForm(baseline); applyBranding(baseline); } };

  if (loading || !form) {
    return <div className="p-6" data-testid="settings-branding-loading"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  }

  return (
    <div className="p-6 space-y-6 pb-24" data-testid="settings-branding-page">
      <PageHeader
        testId="settings-branding-header"
        title="Branding"
        subtitle="Runtime overrides for primary color, accent color, and font family. Applied on every session boot from `/api/settings/branding`."
        actions={
          <Button variant="outline" onClick={() => navigate("/settings")} data-testid="settings-branding-back">
            Back to Settings
          </Button>
        }
      />

      {/* Live preview */}
      <Card className="rounded-md" data-testid="settings-branding-preview">
        <CardHeader>
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Palette className="h-4 w-4 text-primary" /> Live preview
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2 items-center flex-wrap">
            <Button data-testid="settings-branding-preview-btn-primary">Primary</Button>
            <Button variant="outline" data-testid="settings-branding-preview-btn-outline">Outline</Button>
            <Button variant="secondary">Secondary</Button>
            <Button variant="destructive">Destructive</Button>
            <Badge className="bg-primary/10 text-primary border-primary/20">Primary badge</Badge>
            <Badge className="bg-accent/10 text-accent border-accent/20">Accent badge</Badge>
          </div>
          <div className="text-sm">
            <span className="text-2xl font-bold">Aa</span> — the quick brown fox jumps over the lazy dog.
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-md max-w-2xl">
        <CardHeader>
          <CardTitle className="text-sm font-medium">Palette</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label htmlFor="primary">Primary</Label>
              <div className="flex items-center gap-2">
                <input
                  id="primary" type="color"
                  value={hslStringToHex(form.primary_hsl)}
                  onChange={(e) => setForm((f) => ({ ...f, primary_hsl: hexToHslString(e.target.value) }))}
                  disabled={!canWrite}
                  data-testid="settings-branding-primary-picker"
                  className="h-9 w-14 rounded border border-border p-1 bg-transparent"
                />
                <Input
                  value={form.primary_hsl}
                  onChange={(e) => setForm((f) => ({ ...f, primary_hsl: e.target.value }))}
                  disabled={!canWrite}
                  data-testid="settings-branding-primary-hsl"
                  className="font-mono text-xs"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="accent">Accent</Label>
              <div className="flex items-center gap-2">
                <input
                  id="accent" type="color"
                  value={hslStringToHex(form.accent_hsl)}
                  onChange={(e) => setForm((f) => ({ ...f, accent_hsl: hexToHslString(e.target.value) }))}
                  disabled={!canWrite}
                  data-testid="settings-branding-accent-picker"
                  className="h-9 w-14 rounded border border-border p-1 bg-transparent"
                />
                <Input
                  value={form.accent_hsl}
                  onChange={(e) => setForm((f) => ({ ...f, accent_hsl: e.target.value }))}
                  disabled={!canWrite}
                  data-testid="settings-branding-accent-hsl"
                  className="font-mono text-xs"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-md max-w-2xl">
        <CardHeader>
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Type className="h-4 w-4 text-primary" /> Typography
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-1 max-w-sm">
            <Label>Font family</Label>
            <Select
              value={form.font_family}
              onValueChange={(v) => setForm((f) => ({ ...f, font_family: v }))}
              disabled={!canWrite}
            >
              <SelectTrigger data-testid="settings-branding-font-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {FONT_OPTIONS.map((f) => (
                  <SelectItem key={f} value={f} data-testid={`settings-branding-font-${f.replace(/\s/g, "-")}`}>
                    {f}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {canWrite && (
        <div className="fixed bottom-0 left-56 right-0 bg-background border-t border-border p-3 flex justify-between items-center z-30" data-testid="settings-branding-actions-bar">
          <div className="text-xs text-muted-foreground">
            {dirty ? "Unsaved changes (live preview active)" : "All saved"}
          </div>
          <div className="flex gap-2">
            {canReset && (
              <Button variant="outline" onClick={handleReset} data-testid="settings-branding-reset-btn">
                <RotateCcw className="h-4 w-4" /> Reset to defaults
              </Button>
            )}
            <Button variant="outline" onClick={handleCancel} disabled={!dirty} data-testid="settings-branding-cancel-btn">
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!dirty || saving} data-testid="settings-branding-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              <span className="ml-1">Save changes</span>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
